/**
 * Slider — frontend bootstrap (Embla + auto-advance + controls registry).
 *
 * Reuses the globally-enqueued classic scripts: `brandy-embla-script`
 * (window.EmblaCarousel), `brandy-embla-autoplay` (window.EmblaCarouselAutoplay),
 * `brandy-embla-auto-scroll` (window.EmblaCarouselAutoScroll) and
 * `brandy-embla-registry` (window.brandyEmbla). Module scripts can run
 * before deferred classic scripts, so each global is awaited with a short poll
 * — same pattern as MediaGallery/view.js.
 *
 * @package BrandyBlocks
 */

const SLIDER_SELECTOR = '[data-brandy-slider]';

/**
 * Poll for a window global defined by a deferred classic script.
 * Resolves the global or null after timeout (with a console warning).
 */
async function waitForGlobal(name, timeoutMs = 2000) {
  const start = Date.now();
  while (!window[name]) {
    if (Date.now() - start > timeoutMs) {
      // eslint-disable-next-line no-console
      console.warn(`[brandy/slider] ${name} global not found within timeout`);
      return null;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  return window[name];
}

/**
 * Toggle `embla--no-loop`, which drops the last slide's trailing margin.
 *
 * That margin spaces the wrap-around in loop mode, but without loop it is pure
 * overflow: Embla reads it as a gap's worth of scrollable slack, so a slider
 * whose slides all already fit still enables the next arrow and renders a
 * second dot. Embla drops loop itself when there are too few slides to wrap,
 * so keying off the resolved option covers both the explicitly non-looping
 * slider and the too-few-slides one. Re-synced on reInit because
 * slides-per-view is a breakpoint value: a resize can flip looping.
 */
function syncTrailingGap(api, emblaNode) {
  let reentrant = false;

  const sync = () => {
    if (reentrant) return;
    const isNoLoop = !api.internalEngine().options.loop;
    if (isNoLoop === emblaNode.classList.contains('embla--no-loop')) return;
    emblaNode.classList.toggle('embla--no-loop', isNoLoop);
    // Re-measure: the trailing gap just appeared or disappeared. Guarded so the
    // reInit this fires cannot re-enter and loop forever.
    reentrant = true;
    api.reInit();
    reentrant = false;
  };

  api.on('reInit', sync);
  sync();
}

/**
 * Give the arrows and dots authority over a drifting slider.
 *
 * While auto-scroll plays it swaps Embla's scroll body for a constant-velocity
 * one that has no notion of a target, so scrollNext/scrollTo from the controls
 * resolve to nothing and the strip carries on drifting — the controls look
 * dead. Standing the plugin down for the length of the move puts the real
 * scroll body back; the drift resumes once the slide has settled.
 *
 * Listening on the containers rather than the buttons is deliberate: dots are
 * rendered into `.embla__dots` afterwards by the controls registry, and theme
 * variants are free to render whatever markup they like inside it.
 */
function yieldAutoScrollToControls(api, emblaNode, viewportNode) {
  const autoScroll = api.plugins()?.autoScroll;
  if (!autoScroll) return;

  const resume = () => {
    api.off('settle', resume);
    // A pointer resting on the strip means the hover pause owns the plugin's
    // state — resuming here would cancel a pause the reader still wants, and
    // the plugin's own mouseleave will start it again on the way out.
    if (!viewportNode.matches(':hover')) autoScroll.play();
  };

  /*
   * A grouped placement puts the arrows and dots together in `.embla__controls`
   * and emits no `.embla__buttons` at all, so listening for that container
   * alone would leave the arrows unyielded — they would click into a drifting
   * strip and do nothing, while the dots (still in their own container) kept
   * working. Prefer the grouped wrapper when it is present; it holds both.
   */
  const grouped = emblaNode.querySelector('.embla__controls');

  (grouped
    ? [grouped]
    : [
        emblaNode.querySelector('.embla__buttons'),
        emblaNode.querySelector('.embla__dots'),
      ]
  )
    .filter(Boolean)
    .forEach((node) => {
      /*
       * Capture phase, and `click` rather than a pointer event. Capture is what
       * puts this ahead of the button's own handler, so the scroll body is back
       * in place before scrollNext runs; `click` is what covers the keyboard,
       * where activating a focused arrow fires no pointer event at all.
       */
      node.addEventListener(
        'click',
        () => {
          autoScroll.stop();
          // Re-arm rather than stack: a second click mid-move must not leave an
          // earlier listener behind to resume the drift under the new one.
          api.off('settle', resume);
          api.on('settle', resume);
        },
        true
      );
    });
}

async function initSlider(sliderEl) {
  const Embla = await waitForGlobal('EmblaCarousel');
  if (!Embla) return;

  const emblaNode = sliderEl.querySelector('.embla');
  const viewportNode = sliderEl.querySelector('.embla__viewport');
  if (!emblaNode || !viewportNode) return;

  const loop = emblaNode.dataset.loop !== 'false';

  const plugins = [];
  // Shared reader: accepts both the canonical data-autoplay* attributes and the
  // legacy data-auto-play* spelling, and both "true"/"1" encodings. The registry
  // is resolved once here and reused for the controls below. waitForGlobal can
  // time out, so a missing registry falls back to reading the canonical
  // attributes inline rather than taking the whole slider down with it.
  const registry = await waitForGlobal('brandyEmbla');
  const autoplayConfig = registry
    ? registry.readAutoplayConfig(emblaNode, { delay: 4000 })
    : {
        enabled: emblaNode.dataset.autoplay === 'true',
        delay: parseInt(emblaNode.dataset.autoplayDelay, 10) || 4000,
        mode: emblaNode.dataset.autoplayMode === 'scroll' ? 'scroll' : 'step',
        pauseOnHover: emblaNode.dataset.pauseHover === 'true',
      };
  const autoAdvance = autoplayConfig.enabled;
  const mode = autoplayConfig.mode;
  /*
   * Continuous drift is the case `prefers-reduced-motion` exists for: it never
   * settles, so there is no moment at which the movement stops asking for
   * attention. Honouring the setting means leaving the slider still, not
   * quietly demoting it to timed paging — that would be the same refusal
   * answered with a different animation. Timed paging asked for outright is
   * left alone: it is the author pacing discrete content, and Embla stops it
   * on the first interaction anyway.
   */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (autoAdvance && mode === 'scroll' && !reduceMotion) {
    const AutoScroll = await waitForGlobal('EmblaCarouselAutoScroll');
    if (AutoScroll) {
      plugins.push(
        AutoScroll({
          speed: parseFloat(emblaNode.dataset.autoscrollSpeed) || 2,
          /*
           * Halting for good the first time someone drags would turn a strip
           * that reads as ambient into one that looks broken, so a drag only
           * displaces the drift rather than ending it. Hover still pauses when
           * the author asked for it — that one is a deliberate "let me read
           * this" gesture, and it resumes on its own.
           */
          stopOnInteraction: false,
          stopOnMouseEnter: autoplayConfig.pauseOnHover,
          /*
           * Every resume runs through the same play() the plugin uses at init,
           * so its 1s startDelay lands on mouse-leave too: the strip sits
           * still for a beat after the pointer has already gone, which reads
           * as a hang rather than as a pause the reader controls. Zero keeps
           * the delay off both paths — the strip is drifting on arrival and
           * picks up the instant the pointer leaves.
           */
          startDelay: 0,
        })
      );
    }
    // Missing plugin global degrades to a manual slider — already warned.
  } else if (autoAdvance && mode === 'step') {
    const Autoplay = await waitForGlobal('EmblaCarouselAutoplay');
    if (Autoplay) {
      plugins.push(
        Autoplay({
          delay: autoplayConfig.delay,
          stopOnInteraction: true,
          /*
           * Hover belongs to the registry helper attached after init: the
           * plugin's own stopOnMouseEnter throws away the half-run delay, and
           * binds no mouseleave at all while stopOnInteraction is true — which
           * is how a hover used to stop the slider for good.
           */
          stopOnMouseEnter: false,
        })
      );
    }
    // Missing plugin global degrades to a manual slider — already warned.
  }

  // Drop the no-JS scroll fallback BEFORE init: an overflow-x:auto container
  // clips its own slides, which breaks Embla's translate-based paging (blank
  // slider after next/prev) and skews its size measurements. Reset any scroll
  // the user performed pre-init so slide 1 is the measured origin.
  const containerNode = emblaNode.querySelector('.embla__container');
  if (containerNode) containerNode.scrollLeft = 0;
  sliderEl.classList.add('is-embla-ready');

  const api = Embla(viewportNode, { loop, align: 'start' }, plugins);

  // Re-measure without the phantom trailing gap before anything reads the snap
  // count, so the guard below and the dots wire up against the real range.
  syncTrailingGap(api, emblaNode);

  /*
   * Slide state for CSS-only effects (.is-selected, --brandy-slide-progress).
   * Wired before the single-snap bail below on purpose: a slider with nothing
   * to scroll still has an active slide, and an effect keyed off .is-selected
   * would otherwise sit in its parked state — a settle animation that never
   * settles. Optional-chained for the same reason the rest of the registry is:
   * a timed-out global should cost the slider its effects, not its navigation.
   */
  const detachMotion = registry?.motion?.attach(api, emblaNode);
  if (typeof detachMotion === 'function') api.on('destroy', detachMotion);

  /*
   * Which edges still hide something, for the fade mask in embla.min.css. Wired
   * before the single-snap bail below for the same reason motion is: a slider
   * with nothing to scroll still has to be told so, or it would keep whichever
   * edge classes a previous measurement left on it.
   */
  const detachFade = registry?.fade?.attach(api, emblaNode);
  if (typeof detachFade === 'function') api.on('destroy', detachFade);

  /*
   * Hover pause that holds the remaining delay, so the progress fill and the
   * slide it counts down agree on when the next one is due. A no-op under
   * auto-scroll, which keeps its own hover handling: a drift has no delay to
   * hold. Optional-chained like the rest of the registry — a timed-out global
   * costs the slider its hover pause, not its paging.
   */
  if (autoplayConfig.pauseOnHover) {
    const detachHoverPause = registry?.attachHoverPause?.(api, viewportNode);
    if (typeof detachHoverPause === 'function') api.on('destroy', detachHoverPause);
  }

  // Nothing to scroll: hide the chrome instead of showing dead controls
  // (mirrors embla-extra.js behavior for single-snap sliders). A grouped
  // placement replaces .embla__buttons with .embla__controls, so both are hidden.
  if (api.scrollSnapList().length <= 1) {
    emblaNode.querySelector('.embla__buttons')?.style.setProperty('display', 'none');
    emblaNode.querySelector('.embla__controls')?.style.setProperty('display', 'none');
    emblaNode.querySelector('.embla__dots')?.style.setProperty('display', 'none');
    return;
  }

  // Nav + dots from the shared controls registry (window.brandyEmbla),
  // resolved from data-nav-style / data-dots-style with a default fallback —
  // theme-registered variants work here automatically.
  const be = registry;
  if (!be) return;

  // Gate on the buttons themselves, not their wrapper: a grouped placement
  // renders them inside .embla__controls with no .embla__buttons at all.
  if (emblaNode.querySelector('.embla__button--prev')) {
    const navFn = be.nav.resolve(emblaNode.getAttribute('data-nav-style') || 'default');
    if (navFn) {
      const cleanup = navFn(api, emblaNode);
      if (typeof cleanup === 'function') api.on('destroy', cleanup);
    }
  }
  if (emblaNode.querySelector('.embla__dots')) {
    const dotsFn = be.dots.resolve(emblaNode.getAttribute('data-dots-style') || 'default');
    if (dotsFn) {
      const cleanup = dotsFn(api, emblaNode);
      if (typeof cleanup === 'function') api.on('destroy', cleanup);
    }
  }

  yieldAutoScrollToControls(api, emblaNode, viewportNode);
}

function bootstrap() {
  document.querySelectorAll(SLIDER_SELECTOR).forEach((el) => {
    initSlider(el).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[brandy/slider] Embla init failed', err);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
